import React, { useState, useEffect, useRef } from "react";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  closeIcon,
  zoomInIcon,
  zoomOutIcon,
} from "../../assets/icons/SvgIconList";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { resolveFileUrl } from "../../utils/SignedFileUrl";
import { Document, Page, pdfjs } from "react-pdf";

interface IPdfViewerProps {
  pdfFile: string;
  title: string;
  onClose: () => void;
}
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const PdfViewer: React.FC<IPdfViewerProps> = ({
  pdfFile,
  title,
  onClose,
}) => {
  const { t } = UseTranslationHook();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfSrcUrl, setPdfSrcUrl] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    const fetchPdf = async () => {
      const signed = await resolveFileUrl(pdfFile);
      if (!cancelled) setPdfSrcUrl(signed ?? undefined);
    };

    fetchPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfFile]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const scrollPercentage =
        container.scrollTop / (container.scrollHeight - container.clientHeight);
      const nextPage = Math.ceil(numPages * scrollPercentage);

      if (nextPage !== 0) setPageNumber(nextPage);
    }
  };

  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  };

  return (
    <div className="fixed top-0 left-0 h-svh w-screen bg-invert flex justify-center items-center z-50">
      <div className="bg-subtle p-2 rounded-lg h-svh w-full flex flex-col items-center relative">
        <div className="flex justify-between w-full z-50  h-10 items-center">
          <div className="text-lg font-semibold">{title}</div>
          <div className="size-5 cursor" onClick={onClose}>
            {closeIcon}
          </div>
        </div>

        <div
          className="flex-1 overflow-auto scrollbar-none"
          ref={containerRef}
          onScroll={handleScroll}
        >
          {pdfSrcUrl ? (
            <Document file={pdfSrcUrl} onLoadSuccess={onDocumentLoadSuccess}>
              {Array.from(new Array(numPages), (el, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={window.innerWidth * zoom}
                />
              ))}
            </Document>
          ) : (
            <p>{t("failed_load")}</p>
          )}
        </div>

        <div className="flex justify-center items-center space-x-4 mt-4">
          <div className=" px-4 py-2 bg-inset rounded" onClick={handleZoomOut}>
            <div className="size-5">{zoomOutIcon}</div>
          </div>
          <div className="flex justify-center items-center font-semibold">
            <span>
              {t("page")} {pageNumber} {t("of")} {numPages}
            </span>
          </div>
          <div className=" px-4 py-2 bg-inset rounded" onClick={handleZoomIn}>
            <div className="size-5">{zoomInIcon}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;
